/* ---------------------------------------------------------------------------
   About page (templates/page.about-us.liquid, sections/te-about-*.liquid). Styles: assets/te-about.css.

   - Parts marked data-te-reveal get the class is-inview when they are reached (once). The template adds the class
     te-about--anim to the page only when that movement is wanted (not in the theme editor, not with "reduce motion"),
     and shows everything if this file never arrives.
   - The number of brands counts up when its fact is reached.
   - The opening photo moves slightly slower than the page while it is on screen.
   - The photo row is a keyboard stop while it scrolls sideways (phones).
   - Links to a part of the page ("Our story") stop below the sticky header: the theme's own handler for "#" links
     (theme.js) only allows for the announcement bar, so this one runs first (capture phase).
   --------------------------------------------------------------------------- */

(function () {
  'use strict';

  var page = document.querySelector('[data-te-about]');

  if (!page) {
    return;
  }

  var reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var animated = page.classList.contains('te-about--anim') && 'IntersectionObserver' in window;

  // "120+" counts up from 0 once
  function countUp(element) {
    var total = parseInt(element.getAttribute('data-te-about-count'), 10);
    var suffix = element.getAttribute('data-suffix') || '';
    var duration = 1400;
    var start = null;

    if (!total || element.teAboutCounted) {
      return;
    }

    element.teAboutCounted = true;

    function step(now) {
      var progress;

      if (start === null) {
        start = now;
      }

      progress = Math.min((now - start) / duration, 1);
      // Fast at first, slowing towards the end
      element.textContent = Math.round(total * (1 - Math.pow(1 - progress, 3))) + suffix;

      if (progress < 1) {
        window.requestAnimationFrame(step);
      }
    }

    element.textContent = '0' + suffix;
    window.requestAnimationFrame(step);
  }

  function reveal(element) {
    var row = element.closest('[data-te-about-row]');

    element.classList.add('is-inview');
    element.querySelectorAll('[data-te-about-count]').forEach(countUp);

    // Photos in the swipe row open together, including the ones still off the side of a phone's screen
    if (row && !row.teAboutRevealed) {
      row.teAboutRevealed = true;
      row.querySelectorAll('[data-te-reveal]').forEach(reveal);
    }
  }

  if (animated) {
    var revealObserver = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          reveal(entry.target);
          revealObserver.unobserve(entry.target);
        }
      });
    }, { threshold: 0.12, rootMargin: '0px 0px -6% 0px' });

    page.querySelectorAll('[data-te-reveal]').forEach(function (element) {
      // The opening is at the top of the page: it starts at once, photo included, even if the photo is below the fold
      if (element.closest('.te-about-hero')) {
        reveal(element);
      } else {
        revealObserver.observe(element);
      }
    });

    page.setAttribute('data-te-about-ready', '');
  }

  // The opening photo is 12% taller than its window (te-about.css): it is moved by up to 5% of the window's height,
  // against the scroll, while any of it is on screen
  var parallax = animated && page.querySelector('[data-te-about-parallax]');

  if (parallax) {
    var parallaxImage = parallax.querySelector('img');
    var parallaxOn = false;
    var parallaxQueued = false;

    var moveParallax = function () {
      var rect = parallax.getBoundingClientRect();
      var viewport = window.innerHeight || document.documentElement.clientHeight;
      var progress = (rect.top + rect.height / 2 - viewport / 2) / viewport;

      parallaxQueued = false;
      progress = Math.max(-1, Math.min(1, progress));
      parallaxImage.style.setProperty('--te-about-shift', (progress * rect.height * 0.05).toFixed(1) + 'px');
    };

    var queueParallax = function () {
      if (parallaxOn && !parallaxQueued) {
        parallaxQueued = true;
        window.requestAnimationFrame(moveParallax);
      }
    };

    if (parallaxImage) {
      new IntersectionObserver(function (entries) {
        parallaxOn = entries[0].isIntersecting;
        queueParallax();
      }).observe(parallax);

      window.addEventListener('scroll', queueParallax, { passive: true });
      window.addEventListener('resize', queueParallax);
    }
  }

  // The photo row scrolls sideways on phones only. While it does, the keyboard can reach it (arrow keys then scroll
  // it); from tablets up, where the photos sit side by side, it is not a stop.
  var scroller = page.querySelector('[data-te-about-scroller]');

  if (scroller) {
    var updateScroller = function () {
      if (scroller.scrollWidth > scroller.clientWidth + 1) {
        scroller.setAttribute('tabindex', '0');
        scroller.setAttribute('role', 'region');
        scroller.setAttribute('aria-label', 'Photos of the space');
      } else {
        scroller.removeAttribute('tabindex');
        scroller.removeAttribute('role');
        scroller.removeAttribute('aria-label');
      }
    };

    updateScroller();
    window.addEventListener('resize', updateScroller);
  }

  document.addEventListener('click', function (event) {
    var link = event.target.closest('.te-about a[href^="#"]');
    var target;

    if (!link || link.getAttribute('href').length < 2 || event.ctrlKey || event.metaKey || event.shiftKey) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();

    target = document.getElementById(link.getAttribute('href').slice(1));

    if (!target) {
      return;
    }

    if (!target.hasAttribute('tabindex')) {
      target.setAttribute('tabindex', '-1');
    }

    target.scrollIntoView({ behavior: reducedMotion ? 'auto' : 'smooth', block: 'start' });
    target.focus({ preventScroll: true });
  }, true);
})();
